// Stylised motorbike / scooter diagram for the damage sheet.
// Keep colours minimal: white fill, slate-900 stroke, so the markers
// (numbered dots) remain the visual focus. All shapes live inside a
// 100×60 viewBox so the same %-based marker coords work across views.

const STROKE = "#0f172a";   // slate-900
const STROKE_W = 0.5;
const FILL = "#ffffff";

function MotoFront() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round" strokeLinecap="round">
      {/* Windshield */}
      <path d="M40 12 Q 50 8 60 12 L 58 20 L 42 20 Z" />
      {/* Front fairing / headlight panel */}
      <path d="M34 20 Q 50 16 66 20 L 64 32 Q 50 30 36 32 Z" />
      {/* Headlight */}
      <ellipse cx="50" cy="26" rx="6" ry="3.5" fill={STROKE} />
      {/* Handlebars */}
      <line x1="22" y1="22" x2="34" y2="22" />
      <line x1="78" y1="22" x2="66" y2="22" />
      {/* Mirrors */}
      <circle cx="20" cy="20" r="2" />
      <circle cx="80" cy="20" r="2" />
      {/* Fork tubes */}
      <line x1="44" y1="32" x2="44" y2="48" />
      <line x1="56" y1="32" x2="56" y2="48" />
      {/* Front wheel */}
      <circle cx="50" cy="50" r="7" />
      <circle cx="50" cy="50" r="2.5" fill={STROKE} />
      {/* Fender */}
      <path d="M42 48 Q 50 44 58 48" />
    </g>
  );
}

function MotoBack() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round" strokeLinecap="round">
      {/* Rider seat profile */}
      <path d="M36 14 Q 50 10 64 14 L 62 24 L 38 24 Z" />
      {/* Tail / rear cowling */}
      <path d="M32 24 L 68 24 L 64 36 L 36 36 Z" />
      {/* Tail light */}
      <rect x="44" y="28" width="12" height="4" rx="1" fill={STROKE} />
      {/* License plate */}
      <rect x="40" y="38" width="20" height="6" rx="1" />
      {/* Exhaust pipes */}
      <rect x="22" y="36" width="6" height="3" rx="1.5" />
      <rect x="72" y="36" width="6" height="3" rx="1.5" />
      {/* Rear wheel */}
      <circle cx="50" cy="50" r="7" />
      <circle cx="50" cy="50" r="2.5" fill={STROKE} />
      {/* Fender */}
      <path d="M42 50 Q 50 54 58 50" />
    </g>
  );
}

function MotoSide({ flip = false }) {
  return (
    <g
      fill={FILL}
      stroke={STROKE}
      strokeWidth={STROKE_W}
      strokeLinejoin="round"
      strokeLinecap="round"
      transform={flip ? "translate(100 0) scale(-1 1)" : undefined}
    >
      {/* Front wheel */}
      <circle cx="20" cy="46" r="7" />
      <circle cx="20" cy="46" r="2.5" fill={STROKE} />
      {/* Rear wheel */}
      <circle cx="80" cy="46" r="7" />
      <circle cx="80" cy="46" r="2.5" fill={STROKE} />
      {/* Front forks */}
      <line x1="20" y1="46" x2="28" y2="28" />
      <line x1="20" y1="46" x2="30" y2="32" />
      {/* Handlebar / front fairing */}
      <path d="M26 26 Q 32 22 38 24 L 38 30 L 26 30 Z" />
      {/* Headlight */}
      <circle cx="28" cy="28" r="2" fill={STROKE} />
      {/* Fuel tank */}
      <path d="M38 26 Q 48 22 56 26 L 56 36 L 38 36 Z" />
      {/* Seat */}
      <path d="M56 30 L 76 30 Q 78 32 78 36 L 56 36 Z" />
      {/* Rear cowl */}
      <path d="M70 30 L 84 30 L 84 38 L 76 38 Z" />
      {/* Engine block */}
      <rect x="40" y="36" width="18" height="8" rx="1.5" />
      {/* Swingarm */}
      <line x1="58" y1="42" x2="80" y2="46" />
      {/* Exhaust pipe */}
      <path d="M52 44 Q 70 46 84 44" />
      {/* Front fender */}
      <path d="M14 38 Q 20 36 26 40" />
      {/* Side mirror */}
      <line x1="32" y1="22" x2="34" y2="18" />
      <circle cx="34" cy="17" r="1.5" />
    </g>
  );
}

function MotoTop() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round" strokeLinecap="round">
      {/* Front wheel (top down) */}
      <rect x="46" y="6" width="8" height="6" rx="1" />
      {/* Handlebars */}
      <line x1="30" y1="16" x2="70" y2="16" strokeWidth="1.2" />
      {/* Grips */}
      <rect x="26" y="14.5" width="6" height="3" rx="1.5" fill={STROKE} />
      <rect x="68" y="14.5" width="6" height="3" rx="1.5" fill={STROKE} />
      {/* Mirrors */}
      <circle cx="22" cy="14" r="2" />
      <circle cx="78" cy="14" r="2" />
      {/* Fuel tank */}
      <path d="M40 20 Q 50 18 60 20 L 60 32 Q 50 33 40 32 Z" />
      {/* Seat */}
      <path d="M40 34 L 60 34 L 60 46 Q 50 48 40 46 Z" />
      {/* Rear cowling */}
      <path d="M42 46 L 58 46 L 56 52 L 44 52 Z" />
      {/* Rear wheel */}
      <rect x="46" y="54" width="8" height="4" rx="1" />
    </g>
  );
}

export function MotorbikeDiagram({ view }) {
  switch (view) {
    case "front": return <MotoFront />;
    case "back":  return <MotoBack />;
    case "left":  return <MotoSide flip={false} />;
    case "right": return <MotoSide flip={true} />;
    case "top":   return <MotoTop />;
    default:      return <MotoFront />;
  }
}
